import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Sheet, Stack, Skeleton, Button, Grid, Table, Chip, Box, ToggleButtonGroup } from '@mui/joy';
import Dashboard from '../../pages/dashboard/Dashboard.tsx';
import { createApiClient } from '../../api/poshwatch_client.ts';
import { AutomationDetails } from 'poshwatch_ts_client';

type ScheduleTz = 'UTC' | 'America/New_York' | 'local';

const SCHEDULE_TZ_LABELS: Record<ScheduleTz, string> = {
  'UTC': 'UTC',
  'America/New_York': 'Eastern',
  'local': Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const convertHourUtcTo = (utcHour: number, tz: ScheduleTz): number => {
  if (tz === 'UTC') return utcHour;
  const d = new Date();
  d.setUTCHours(utcHour, 0, 0, 0);
  const tzName = tz === 'local' ? undefined : tz;
  return parseInt(d.toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: tzName }), 10);
};

const ROWS_PER_PAGE = 15;

const formatUtcTimestamp = (value?: string | null) => value ? new Date(value).toLocaleString() : '-';
const formatBucketTimestamp = (value?: string | null) => {
  if (!value) return '-';
  const d = new Date(value);
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};
const formatUtcHourLabel = (hour: number | null) => hour === null ? '-' : `${hour.toString().padStart(2, '0')}:00 UTC`;

const PaginationControls = ({
  currentPage,
  totalPages,
  total,
  onPrev,
  onNext
}: {
  currentPage: number;
  totalPages: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) => {
  if (totalPages <= 1) return null;
  return (
    <Stack direction="row" justifyContent="center" spacing={1} alignItems="center" sx={{ mt: 1 }}>
      <Button variant="outlined" size="sm" onClick={onPrev} disabled={currentPage === 1}>Previous</Button>
      <Typography level="body-sm">
        Page {currentPage} of {totalPages} ({total} total)
      </Typography>
      <Button variant="outlined" size="sm" onClick={onNext} disabled={currentPage === totalPages}>Next</Button>
    </Stack>
  );
};

const usePagination = (items: any[]) => {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.ceil(items.length / ROWS_PER_PAGE);
  const paginated = items.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
  return {
    page, totalPages, paginated, total: items.length,
    onPrev: () => setPage(p => Math.max(p - 1, 1)),
    onNext: () => setPage(p => Math.min(p + 1, totalPages)),
  };
};

const AdminUserDetail: React.FC = () => {
  const { userId, closetId } = useParams<{ userId: string; closetId: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = React.useState<AutomationDetails | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [scheduleTz, setScheduleTz] = React.useState<ScheduleTz>('UTC');

  React.useEffect(() => {
    if (!userId || !closetId) return;

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const client = await createApiClient();
        const response = await client.getAutomationDetails(userId, closetId);
        setDetails(response.data);
      } catch (err) {
        console.error('Error fetching automation details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load automation details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [userId, closetId]);

  const shareBuckets = details?.buckets48h.share || [];
  const communityBuckets = details?.buckets48h.community_share || [];
  const relistingBuckets = details?.buckets48h.relisting || [];
  const recentLogs = details?.logs.recent48h || [];
  const relistingSamples = details?.relistingEligibility.samples || [];

  const logsPagination = usePagination(recentLogs);
  const samplesPagination = usePagination(relistingSamples);
  const sharePagination = usePagination(shareBuckets);
  const communityPagination = usePagination(communityBuckets);
  const relistingPagination = usePagination(relistingBuckets);

  return (
    <Dashboard>
      <Container>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Button variant="outlined" size="sm" onClick={() => navigate('/admin')}>
            Back
          </Button>
          <Typography level="h3">
            {details ? details.closetId : closetId}
          </Typography>
        </Stack>

        {isLoading ? (
          <Stack spacing={2}>
            {[...Array(6)].map((_, i) => (
              <Typography key={i}><Skeleton>Loading automation details...</Skeleton></Typography>
            ))}
          </Stack>
        ) : error ? (
          <Typography color="danger">{error}</Typography>
        ) : details ? (
          <Stack spacing={3}>
            {/* Overview */}
            <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
              <Typography level="title-lg" sx={{ mb: 2 }}>Overview</Typography>
              <Grid container spacing={3}>
                <Grid xs={6} sm={3}><Typography level="title-sm">User ID</Typography><Typography level="body-sm" sx={{ wordBreak: 'break-all' }}>{details.userId}</Typography></Grid>
                <Grid xs={6} sm={3}><Typography level="title-sm">Closet ID</Typography><Typography level="body-sm">{details.closetId}</Typography></Grid>
                <Grid xs={6} sm={3}><Typography level="title-sm">Email</Typography><Typography level="body-sm" sx={{ wordBreak: 'break-all' }}>{details.closet.email || '-'}</Typography></Grid>
                <Grid xs={6} sm={3}><Typography level="title-sm">Subscription</Typography><Typography level="body-sm">{details.closet.subscriptionType}</Typography></Grid>
                <Grid xs={6} sm={3}><Typography level="title-sm">Timezone</Typography><Typography level="body-sm">{details.closet.timezone || '-'}</Typography></Grid>
                <Grid xs={6} sm={3}><Typography level="title-sm">Schedule</Typography><Chip size="sm" variant="soft" color={details.closet.scheduleEnabled ? 'success' : 'neutral'}>{details.closet.scheduleEnabled ? 'On' : 'Off'}</Chip></Grid>
                <Grid xs={6} sm={3}><Typography level="title-sm">Community Share</Typography><Chip size="sm" variant="soft" color={details.closet.communityShareEnabled ? 'success' : 'neutral'}>{details.closet.communityShareEnabled ? 'On' : 'Off'}</Chip></Grid>
                <Grid xs={6} sm={3}><Typography level="title-sm">Relisting</Typography><Chip size="sm" variant="soft" color={details.closet.enableRelisting ? 'success' : 'neutral'}>{details.closet.enableRelisting ? 'On' : 'Off'}</Chip></Grid>
                <Grid xs={6} sm={3}><Typography level="title-sm">Last Fetched</Typography><Typography level="body-sm">{formatUtcTimestamp(details.closet.lastFetchedAt)}</Typography></Grid>
              </Grid>
            </Sheet>

            {/* Diagnosis */}
            <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
              <Typography level="title-lg" sx={{ mb: 2 }}>Diagnosis</Typography>
              <Stack spacing={1}>
                <Typography level="body-sm">Community Shares: {details.diagnosis.communityShare.join('; ')}</Typography>
                <Typography level="body-sm">Relisting: {details.diagnosis.relisting.join('; ')}</Typography>
                <Typography level="body-sm">Next Share Hour: {formatUtcHourLabel(details.closet.nextShareHour)}</Typography>
                <Typography level="body-sm">
                  Latest Relisting Bucket: {details.diagnosis.latestRelistingBucket ? `${details.diagnosis.latestRelistingBucket.timestamp} (${details.diagnosis.latestRelistingBucket.count})` : 'none in recent history'}
                </Typography>
              </Stack>
            </Sheet>

            {/* Enabled Share Hours - 24h Grid */}
            <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography level="title-lg">Share Schedule</Typography>
                <ToggleButtonGroup
                  size="sm"
                  value={scheduleTz}
                  onChange={(_e, val) => val && setScheduleTz(val as ScheduleTz)}
                >
                  {(Object.keys(SCHEDULE_TZ_LABELS) as ScheduleTz[]).map(tz => (
                    <Button key={tz} value={tz}>{SCHEDULE_TZ_LABELS[tz]}</Button>
                  ))}
                </ToggleButtonGroup>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 0.5 }}>
                {Array.from({ length: 24 }, (_, hour) => {
                  const enabled = details.closet.shareScheduleHours.includes(hour);
                  const displayHour = convertHourUtcTo(hour, scheduleTz);
                  return (
                    <Box
                      key={hour}
                      sx={{
                        textAlign: 'center',
                        py: 1,
                        borderRadius: 'sm',
                        bgcolor: enabled ? 'primary.softBg' : 'neutral.softBg',
                        border: '1px solid',
                        borderColor: enabled ? 'primary.outlinedBorder' : 'neutral.outlinedBorder',
                        color: enabled ? 'primary.plainColor' : 'neutral.plainColor',
                        fontWeight: enabled ? 'bold' : 'normal',
                        fontSize: 'sm',
                      }}
                    >
                      {displayHour.toString().padStart(2, '0')}
                    </Box>
                  );
                })}
              </Box>
              <Typography level="body-xs" sx={{ mt: 1, color: 'neutral.500' }}>
                {details.closet.shareScheduleHours.length} of 24 hours enabled ({SCHEDULE_TZ_LABELS[scheduleTz]})
              </Typography>
            </Sheet>

            {/* Rolling Counts + Action Gates */}
            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm', height: '100%' }}>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Rolling Counts</Typography>
                  <Sheet sx={{ overflowX: 'auto' }}>
                    <Table size="sm" sx={{ tableLayout: 'auto' }}>
                      <thead>
                        <tr>
                          <th style={{ whiteSpace: 'nowrap' }}>Action</th>
                          <th>24h</th>
                          <th>7d</th>
                          <th>30d</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(details.rollingCounts).map(([action, counts]) => (
                          <tr key={action}>
                            <td style={{ whiteSpace: 'nowrap' }}>{action}</td>
                            <td>{counts.last24h.toLocaleString()}</td>
                            <td>{counts.last7d.toLocaleString()}</td>
                            <td>{counts.last30d.toLocaleString()}</td>
                            <td>{counts.total.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Sheet>
                </Sheet>
              </Grid>
              <Grid xs={12} md={6}>
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm', height: '100%' }}>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Action Gates</Typography>
                  <Table size="sm" sx={{ tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Action</th>
                        <th>Allowed</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(details.actionGates).map(([action, gate]) => (
                        <tr key={action}>
                          <td style={{ whiteSpace: 'nowrap' }}>{action}</td>
                          <td><Chip size="sm" variant="soft" color={gate.allowed ? 'success' : 'danger'}>{gate.allowed ? 'Yes' : 'No'}</Chip></td>
                          <td>{gate.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              </Grid>
            </Grid>

            {/* Buckets */}
            <Grid container spacing={3}>
              <Grid xs={12} md={4}>
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Share Buckets (48h)</Typography>
                  <Table size="sm" sx={{ '& th:last-child, & td:last-child': { textAlign: 'right' } }}>
                    <thead><tr><th>Bucket</th><th style={{ width: 60 }}>Count</th></tr></thead>
                    <tbody>
                      {sharePagination.paginated.map(bucket => (
                        <tr key={bucket.timestamp}>
                          <td>{formatBucketTimestamp(bucket.timestamp)}</td>
                          <td>{bucket.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <PaginationControls currentPage={sharePagination.page} totalPages={sharePagination.totalPages} total={sharePagination.total} onPrev={sharePagination.onPrev} onNext={sharePagination.onNext} />
                </Sheet>
              </Grid>
              <Grid xs={12} md={4}>
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Community Share Buckets (48h)</Typography>
                  <Table size="sm" sx={{ '& th:last-child, & td:last-child': { textAlign: 'right' } }}>
                    <thead><tr><th>Bucket</th><th style={{ width: 60 }}>Count</th></tr></thead>
                    <tbody>
                      {communityPagination.paginated.map(bucket => (
                        <tr key={bucket.timestamp}>
                          <td>{formatBucketTimestamp(bucket.timestamp)}</td>
                          <td>{bucket.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <PaginationControls currentPage={communityPagination.page} totalPages={communityPagination.totalPages} total={communityPagination.total} onPrev={communityPagination.onPrev} onNext={communityPagination.onNext} />
                </Sheet>
              </Grid>
              <Grid xs={12} md={4}>
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Relisting Buckets</Typography>
                  <Table size="sm" sx={{ '& th:last-child, & td:last-child': { textAlign: 'right' } }}>
                    <thead><tr><th>Bucket</th><th style={{ width: 60 }}>Count</th></tr></thead>
                    <tbody>
                      {relistingPagination.paginated.map(bucket => (
                        <tr key={bucket.timestamp}>
                          <td>{formatBucketTimestamp(bucket.timestamp)}</td>
                          <td>{bucket.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <PaginationControls currentPage={relistingPagination.page} totalPages={relistingPagination.totalPages} total={relistingPagination.total} onPrev={relistingPagination.onPrev} onNext={relistingPagination.onNext} />
                </Sheet>
              </Grid>
            </Grid>

            {/* Current Tasks */}
            <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
              <Typography level="title-lg" sx={{ mb: 2 }}>Current Tasks</Typography>
              <Sheet sx={{ overflowX: 'auto' }}>
                <Table size="sm" sx={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Status</th>
                      <th>Processed</th>
                      <th>Updated</th>
                      <th>ETA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(details.currentTasks).map(([action, task]) => (
                      <tr key={action}>
                        <td style={{ whiteSpace: 'nowrap' }}>{action}</td>
                        <td>{task?.status || '-'}</td>
                        <td>{task ? `${task.processed}/${task.total}` : '-'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatUtcTimestamp(task?.date)}</td>
                        <td>{task?.eta || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            </Sheet>

            {/* Activity Summaries */}
            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Activity Summary (24h)</Typography>
                  <Table size="sm">
                    <thead><tr><th>Action</th><th>Total</th><th>Errors</th></tr></thead>
                    <tbody>
                      {details.logs.summary24h.map(row => (
                        <tr key={`24h-${row.action}`}>
                          <td style={{ whiteSpace: 'nowrap' }}>{row.action}</td>
                          <td>{row.total}</td>
                          <td>{row.errors > 0 ? <Typography color="danger">{row.errors}</Typography> : row.errors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              </Grid>
              <Grid xs={12} md={6}>
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
                  <Typography level="title-lg" sx={{ mb: 2 }}>Activity Summary (48h)</Typography>
                  <Table size="sm">
                    <thead><tr><th>Action</th><th>Total</th><th>Errors</th></tr></thead>
                    <tbody>
                      {details.logs.summary48h.map(row => (
                        <tr key={`48h-${row.action}`}>
                          <td style={{ whiteSpace: 'nowrap' }}>{row.action}</td>
                          <td>{row.total}</td>
                          <td>{row.errors > 0 ? <Typography color="danger">{row.errors}</Typography> : row.errors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              </Grid>
            </Grid>

            {/* Relisting Eligibility */}
            <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
              <Typography level="title-lg" sx={{ mb: 2 }}>Relisting Eligibility</Typography>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid xs={4} sm={2}><Typography level="title-sm">Total Posts</Typography><Typography level="h4">{details.relistingEligibility.totalPosts}</Typography></Grid>
                <Grid xs={4} sm={2}><Typography level="title-sm">Available</Typography><Typography level="h4">{details.relistingEligibility.availablePosts}</Typography></Grid>
                <Grid xs={4} sm={2}><Typography level="title-sm">Eligible 60+ Days</Typography><Typography level="h4">{details.relistingEligibility.eligible60dPlus}</Typography></Grid>
                <Grid xs={4} sm={2}><Typography level="title-sm">Sold Units</Typography><Typography level="h4">{details.relistingEligibility.skippedSoldUnits ?? '-'}</Typography></Grid>
                <Grid xs={4} sm={2}><Typography level="title-sm">Active Offers</Typography><Typography level="h4">{details.relistingEligibility.skippedActiveOffers ?? '-'}</Typography></Grid>
                <Grid xs={4} sm={2}><Typography level="title-sm">Missing Date</Typography><Typography level="h4">{details.relistingEligibility.missingPublishedDate}</Typography></Grid>
              </Grid>
              <Sheet sx={{ overflowX: 'auto' }}>
                <Table size="sm" sx={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Age Days</th>
                      <th>First Published</th>
                      <th>Title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {samplesPagination.paginated.map(sample => (
                      <tr key={sample.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{sample.id}</td>
                        <td>{sample.ageDays}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatUtcTimestamp(sample.firstPublishedAt)}</td>
                        <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sample.title}>{sample.title}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
              <PaginationControls currentPage={samplesPagination.page} totalPages={samplesPagination.totalPages} total={samplesPagination.total} onPrev={samplesPagination.onPrev} onNext={samplesPagination.onNext} />
            </Sheet>

            {/* Recent Activity */}
            <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'sm' }}>
              <Typography level="title-lg" sx={{ mb: 2 }}>Recent Activity (48h)</Typography>
              <Sheet sx={{ overflowX: 'auto' }}>
                <Table size="sm" sx={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th style={{ width: 180 }}>Date</th>
                      <th style={{ width: 100 }}>Type</th>
                      <th style={{ width: 120 }}>Action</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsPagination.paginated.map((log, index) => (
                      <tr key={`${log.date}-${(logsPagination.page - 1) * ROWS_PER_PAGE + index}`}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatUtcTimestamp(log.date)}</td>
                        <td>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={log.type === 'error' ? 'danger' : log.type === 'task_complete' ? 'success' : log.type === 'warning' ? 'warning' : 'neutral'}
                          >
                            {log.type}
                          </Chip>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{log.action || '-'}</td>
                        <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.message}>{log.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
              <PaginationControls currentPage={logsPagination.page} totalPages={logsPagination.totalPages} total={logsPagination.total} onPrev={logsPagination.onPrev} onNext={logsPagination.onNext} />
            </Sheet>
          </Stack>
        ) : null}
      </Container>
    </Dashboard>
  );
};

export default AdminUserDetail;
